import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Calendar, Clock, Users, ChevronRight, Zap, ArrowLeft, Share2, Check, Pencil, Trash2, X, Save, Camera, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders, getAuthToken } from "@/lib/firebase";
import { MetaPixel } from "@/lib/metapixel";
import { formatEventDate } from "@/lib/dates";
import { useLocationCoords } from "@/hooks/useLocationCoords";
import AvatarStack from "@/components/AvatarStack";
import EventGallery from "@/components/EventGallery";
import UserProfileModal from "@/components/UserProfileModal";

const sportImages = {
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
  football: "https://images.unsplash.com/photo-1575361204480-aadea25e6e68?w=800",
  padel: "https://images.unsplash.com/photo-1612534847738-b3af3b1a5bf6?w=800",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800",
};

const UK_CITIES = ["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Leeds", "Glasgow", "Bristol"];
const EU_CITIES = ["Berlin", "Amsterdam", "Paris"];
const CITY_FLAGS = { Berlin: "🇩🇪", Amsterdam: "🇳🇱", Paris: "🇫🇷" };
const currencyForCity = (city) => UK_CITIES.includes(city) ? "GBP" : EU_CITIES.includes(city) ? "EUR" : "USD";

const GamePage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [game, setGame] = useState(null);
  // Auto-geocode when the game was created before the Find button was wired.
  const resolvedCoords = useLocationCoords({
    latitude: game?.latitude,
    longitude: game?.longitude,
    address: game?.address,
    location_name: game?.location_name,
    city: game?.city,
  });
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [waitlistStatus, setWaitlistStatus] = useState({ on_waitlist: false, position: null });
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [hasBooked, setHasBooked] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const [allAttendees, setAllAttendees] = useState([]);
  const [seriesInfo, setSeriesInfo] = useState(null);

  const getCityWithFlag = (city) => {
    if (CITY_FLAGS[city]) return `${CITY_FLAGS[city]} ${city}`;
    const isUK = UK_CITIES.includes(city);
    return `${isUK ? "🇬🇧" : "🇺🇸"} ${city}`;
  };

  const formatPrice = (price, currency = "USD") => {
    if (price === 0) return "Free";
    const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
    return `${symbol}${price}`;
  };

  const formatDate = (dateStr) => formatEventDate(dateStr, { weekday: "short", day: "numeric", month: "short" });

  const spotsLeft = (g) => Math.max(0, g.max_players - g.current_players);

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";

  const getSportBadgeClass = (sport) => {
    const classes = { basketball: "badge-basketball", football: "badge-football", padel: "badge-padel", volleyball: "badge-volleyball" };
    return classes[sport] || "badge-default";
  };

  const getSkillClass = (level) => {
    const classes = { beginner: "text-emerald-500 dark:text-emerald-400", intermediate: "text-amber-500 dark:text-amber-400", advanced: "text-red-500 dark:text-red-400", all: "text-foreground" };
    return classes[level] || "text-foreground";
  };

  // Fetch game data
  useEffect(() => {
    const fetchGame = async () => {
      try {
        // First try to get from games list (for future events with attendees)
        const response = await fetch(`${API}/games-with-attendees`);
        if (response.ok) {
          const games = await response.json();
          const found = games.find(g => g.game_id === gameId);
          if (found) {
            setGame(found);
            setAttendees(found.attendees_preview || []);
            MetaPixel.viewContent("game", found.game_id, found.title, found.price || 0, found.currency || "GBP");
            setLoading(false);
            return;
          }
        }
        
        // If not found (past event), fetch directly by ID
        const directResponse = await fetch(`${API}/games/${gameId}`);
        if (directResponse.ok) {
          const gameData = await directResponse.json();
          setGame(gameData);
          
          // Public attendee preview (works for past + future, no auth needed)
          const previewResponse = await fetch(`${API}/games/${gameId}/attendees/preview`);
          if (previewResponse.ok) {
            const previewData = await previewResponse.json();
            setAttendees(previewData);
          }
          
          MetaPixel.viewContent("game", gameData.game_id, gameData.title, gameData.price || 0, gameData.currency || "GBP");
        } else {
          toast.error("Game not found");
          navigate("/");
        }
      } catch (error) {
        console.error("Failed to fetch game:", error);
        toast.error("Failed to load game");
      } finally {
        setLoading(false);
      }
    };
    fetchGame();
  }, [gameId, navigate]);

  // If this game is part of a series, fetch a lightweight preview
  useEffect(() => {
    if (!game?.series_id) {
      setSeriesInfo(null);
      return;
    }
    const fetchSeries = async () => {
      try {
        const res = await fetch(`${API}/series/${game.series_id}`);
        if (res.ok) {
          const data = await res.json();
          setSeriesInfo(data);
        }
      } catch (e) {
        console.error("Failed to fetch parent series:", e);
      }
    };
    fetchSeries();
  }, [game?.series_id]);

  // Check ownership (robust to email casing + missing host_email)
  useEffect(() => {
    if (game && user) {
      const userEmail = (user.email || "").toLowerCase().trim();
      const ownerEmail = (game.owner_email || "").toLowerCase().trim();
      const owns = (
        (!!game.owner_id && game.owner_id === user.user_id) ||
        (!!userEmail && !!ownerEmail && userEmail === ownerEmail)
      );
      setIsOwner(owns);
    }
  }, [game, user]);

  // Compute whether the current user can view the player list
  const canViewPlayers = isOwner || hasBooked || !!user?.is_admin;

  // Load all attendees when modal opens (sends auth so server can permission-check)
  useEffect(() => {
    const loadAllAttendees = async () => {
      if (!showPlayersModal || !gameId) return;
      try {
        const headers = user ? await authHeaders() : {};
        const response = await fetch(`${API}/games/${gameId}/attendees`, { headers });
        if (response.ok) {
          const data = await response.json();
          setAllAttendees(data);
        } else if (response.status === 403) {
          setAllAttendees([]);
          toast.error("Only paid attendees, the organizer, or admins can see the player list.");
          setShowPlayersModal(false);
        } else {
          setAllAttendees([]);
        }
      } catch (error) {
        console.error("Failed to load attendees:", error);
        setAllAttendees([]);
      }
    };
    loadAllAttendees();
  }, [showPlayersModal, gameId, user]);

  const handleOpenPlayersModal = () => {
    if (!canViewPlayers) {
      if (!user) {
        toast.error("Sign in and join the game to see who's playing.");
        navigate(`/login?redirect=/game/${gameId}`);
        return;
      }
      toast.error("Only paid attendees, the organizer, or admins can see the player list.");
      return;
    }
    setShowPlayersModal(true);
  };

  // Check if user has already booked this game
  useEffect(() => {
    const checkBookingStatus = async () => {
      if (!gameId || !user) {
        setHasBooked(false);
        return;
      }
      
      try {
        const headers = await authHeaders();
        const response = await fetch(`${API}/reservations`, { headers });
        if (response.ok) {
          const reservations = await response.json();
          const alreadyBooked = reservations.some(r => r.game_id === gameId && r.status === "confirmed");
          setHasBooked(alreadyBooked);
        }
      } catch (error) {
        console.error("Failed to check booking status:", error);
      }
    };
    checkBookingStatus();
  }, [gameId, user]);

  // Fetch waitlist status
  useEffect(() => {
    const fetchWaitlistInfo = async () => {
      if (!gameId) return;
      
      // Get waitlist count (public)
      try {
        const response = await fetch(`${API}/games/${gameId}/waitlist`);
        if (response.ok) {
          const data = await response.json();
          setWaitlistCount(data.count || 0);
        }
      } catch (error) {
        console.error("Failed to fetch waitlist count:", error);
      }
      
      // Get user's waitlist status (if logged in)
      if (user) {
        try {
          const headers = await authHeaders();
          const response = await fetch(`${API}/games/${gameId}/my-waitlist-status`, { headers });
          if (response.ok) {
            const data = await response.json();
            setWaitlistStatus(data);
          }
        } catch (error) {
          console.error("Failed to fetch waitlist status:", error);
        }
      }
    };
    fetchWaitlistInfo();
  }, [gameId, user]);

  const handleJoinWaitlist = async () => {
    if (!user) {
      toast.error("Please login to join the waitlist");
      navigate("/login", { state: { from: `/game/${gameId}` } });
      return;
    }
    
    setJoiningWaitlist(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/games/${gameId}/waitlist`, {
        method: "POST",
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "You've been added to the waitlist!");
        setWaitlistStatus({ on_waitlist: true, position: data.position });
        setWaitlistCount(prev => prev + 1);
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Failed to join waitlist");
      }
    } catch (error) {
      console.error("Waitlist error:", error);
      toast.error("Failed to join waitlist");
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleLeaveWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${gameId}/waitlist`, {
        method: "DELETE",
        headers
      });
      
      if (response.ok) {
        toast.success("You've been removed from the waitlist");
        setWaitlistStatus({ on_waitlist: false, position: null });
        setWaitlistCount(prev => Math.max(0, prev - 1));
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Failed to leave waitlist");
      }
    } catch (error) {
      console.error("Waitlist error:", error);
      toast.error("Failed to leave waitlist");
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleJoinGame = async () => {
    // Fire InitiateCheckout immediately when user clicks Join Game
    if (game) {
      MetaPixel.initiateCheckout("game", game.game_id, game.title, game.price || 0, game.currency || "GBP");
    }
    
    if (!user) {
      toast.error("Please login to join a game");
      navigate("/login", { state: { from: `/game/${gameId}` } });
      return;
    }
    if (!game) return;

    setJoining(true);
    try {
      if (game.price > 0) {
        const headers = await authHeaders({ "Content-Type": "application/json" });
        const response = await fetch(`${API}/payments/checkout`, {
          method: "POST",
          headers,
          body: JSON.stringify({ game_id: game.game_id, origin_url: window.location.origin, apply_wallet: true })
        });
        if (response.ok) {
          const data = await response.json();
          // If wallet covered the full amount, no Stripe redirect needed
          if (data.wallet_only) {
            toast.success("You're in! Wallet credit applied.");
            // Trigger Schedule pixel since this is a successful conversion (no card charge)
            try { MetaPixel.schedule("game", game.game_id, game.title); } catch (e) {}
            // Navigate to profile so they can see their reservation
            navigate("/profile");
            return;
          }
          if (data.wallet_applied > 0) {
            const sym = (game.currency || "USD") === "GBP" ? "£" : (game.currency || "USD") === "EUR" ? "€" : "$";
            toast.success(`${sym}${data.wallet_applied.toFixed(2)} wallet credit applied — paying the rest`);
          }
          window.location.href = data.checkout_url;
        } else {
          const error = await response.clone().json().catch(() => ({}));
          toast.error(error.detail || "Failed to create payment session");
          setJoining(false);
        }
      } else {
        const headers = await authHeaders({ "Content-Type": "application/json" });
        const response = await fetch(`${API}/reservations`, {
          method: "POST",
          headers,
          body: JSON.stringify({ game_id: game.game_id })
        });
        if (response.ok) {
          MetaPixel.schedule("game", game.game_id, game.title);
          toast.success("You're in! See you at the game");
          const gameResponse = await fetch(`${API}/games-with-attendees`);
          if (gameResponse.ok) {
            const games = await gameResponse.json();
            const updated = games.find(g => g.game_id === gameId);
            if (updated) {
              setGame(updated);
              setAttendees(updated.attendees_preview || []);
            }
          }
        } else {
          const error = await response.clone().json().catch(() => ({}));
          toast.error(error.detail || "Failed to join game");
        }
        setJoining(false);
      }
    } catch (error) {
      toast.error("Failed to join game");
      setJoining(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  // Edit handlers
  const openEditModal = () => {
    const dateStr = game.date ? String(game.date).slice(0, 10) : "";
    setEditData({
      title: game.title || "",
      description: game.description || "",
      sport: game.sport || "football",
      date: dateStr,
      time_start: game.time_start || "",
      time_end: game.time_end || "",
      location_name: game.location_name || "",
      city: game.city || "",
      address: game.address || "",
      latitude: game.latitude || 0,
      longitude: game.longitude || 0,
      max_players: game.max_players || 10,
      price: game.price || 0,
      currency: game.currency || "GBP",
      image_url: game.image_url || sportImages.football,
      host_name: game.host_name || "",
      host_image_url: game.host_image_url || "",
      skill_level: game.skill_level || "all",
      host_contact: game.host_contact || "",
      meeting_info: game.meeting_info || "",
    });
    setImagePreview(null);
    setShowEditModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }

    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const token = await getAuthToken();
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/upload/game-image`);
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            xhr.status >= 200 && xhr.status < 300 ? resolve(data) : reject(new Error(data.detail || "Upload failed"));
          } catch { reject(new Error("Upload failed")); }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        const body = new FormData();
        body.append("file", file);
        xhr.send(body);
      });
      const imageUrl = `${API}/files/${result.storage_path}`;
      setEditData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded");
    } catch (error) {
      toast.error(error.message || "Upload failed");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editData.title || !editData.date || !editData.time_start || !editData.time_end || !editData.city || !editData.location_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editData,
        currency: currencyForCity(editData.city),
        date: new Date(editData.date).toISOString(),
      };
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/games/${gameId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success("Game updated!");
        setShowEditModal(false);
        // Refresh game data
        const refreshResp = await fetch(`${API}/games-with-attendees`);
        if (refreshResp.ok) {
          const games = await refreshResp.json();
          const updated = games.find(g => g.game_id === gameId);
          if (updated) { setGame(updated); setAttendees(updated.attendees_preview || []); }
        }
      } else {
        const err = await response.clone().json().catch(() => ({}));
        toast.error(err.detail || "Failed to update game");
      }
    } catch (error) {
      toast.error("Failed to update game");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGame = async () => {
    setDeleting(true);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${gameId}`, { method: "DELETE", headers });
      if (response.ok) {
        toast.success("Game deleted");
        navigate("/");
      } else {
        const err = await response.clone().json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete game");
      }
    } catch (error) {
      toast.error("Failed to delete game");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading game...</p>
        </div>
      </div>
    );
  }

  if (!game) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="game-page">
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 lg:h-96">
        <img src={game.image_url} alt={game.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <Button variant="secondary" size="icon" onClick={() => navigate("/")} className="bg-background/80 backdrop-blur-sm hover:bg-background">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            {isOwner && (
              <>
                <Button variant="secondary" size="icon" onClick={openEditModal} className="bg-background/80 backdrop-blur-sm hover:bg-background" data-testid="edit-game-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="secondary" size="icon" className="bg-background/80 backdrop-blur-sm hover:bg-destructive/80 hover:text-white" data-testid="delete-game-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this game?</AlertDialogTitle>
                      <AlertDialogDescription>This will permanently delete the game and all reservations. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteGame} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
                        {deleting ? "Deleting..." : "Delete"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            <Button variant="secondary" size="icon" onClick={handleShare} className="bg-background/80 backdrop-blur-sm hover:bg-background" data-testid="share-btn">
              {copied ? <Check className="w-5 h-5 text-primary" /> : <Share2 className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <div className="absolute bottom-4 left-4">
          <span className={`sport-badge ${getSportBadgeClass(game.sport)}`}>{game.sport}</span>
        </div>

        <div className="absolute bottom-4 right-4">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
            spotsLeft(game) <= 2 ? "bg-destructive/90 text-white" : spotsLeft(game) === 0 ? "bg-muted text-muted-foreground" : "bg-background/90 text-foreground"
          }`}>
            {spotsLeft(game) === 0 ? "Full" : `${spotsLeft(game)} spot${spotsLeft(game) !== 1 ? "s" : ""} left`}
          </span>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mb-3">{game.title}</h1>
            <button 
              onClick={() => {
                if (game.owner_id) {
                  setProfileUserId(game.owner_id);
                } else {
                  toast.info("Profile not available for this host");
                }
              }}
              className="flex items-center gap-3 hover:bg-muted p-2 -ml-2 rounded-lg transition-colors cursor-pointer"
            >
              <Avatar className="w-10 h-10 border-2 border-primary/30">
                <AvatarImage src={game.host_image_url} alt={game.host_name} />
                <AvatarFallback className="bg-primary/20 text-primary font-medium">{getInitials(game.host_name)}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="text-sm text-muted-foreground">Hosted by</p>
                <p className="font-medium">{game.host_name}</p>
              </div>
            </button>
          </div>

          <p className="text-foreground/80 leading-relaxed">{game.description}</p>

          {/* Host info - visible to confirmed attendees, organizer, or admin */}
          {(game.host_contact || game.meeting_info) && (isOwner || hasBooked || !!user?.is_admin) && (
            <div className="p-4 rounded-xl bg-foreground/[0.04] border border-foreground/10 space-y-3" data-testid="host-contact-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-foreground" />
                <span className="font-display uppercase tracking-wider text-xs text-foreground">From the Host</span>
              </div>
              {game.host_contact && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Contact</p>
                  <p className="font-semibold break-words">{game.host_contact}</p>
                </div>
              )}
              {game.meeting_info && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Meeting details</p>
                  <p className="text-sm whitespace-pre-wrap break-words text-foreground/90">{game.meeting_info}</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Calendar className="w-4 h-4 text-primary" /><span>Date</span></div>
              <p className="font-semibold">{formatDate(game.date)}</p>
            </div>
            <div className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Clock className="w-4 h-4 text-primary" /><span>Time</span></div>
              <p className="font-semibold">{game.time_start} - {game.time_end}</p>
            </div>
            <button 
              onClick={handleOpenPlayersModal}
              className="p-4 rounded-xl bg-card border border-border hover:bg-muted transition-colors text-left"
              data-testid="players-card-btn"
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Users className="w-4 h-4 text-primary" /><span>Players</span></div>
              <p className="font-semibold">{game.current_players}/{game.max_players}<span className="text-sm font-normal text-muted-foreground ml-1">({spotsLeft(game)} left)</span></p>
              {attendees.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <AvatarStack attendees={attendees} max={4} size="xs" />
                  <span className="text-xs font-medium text-foreground/70 underline underline-offset-2">
                    {canViewPlayers ? "View all →" : "🔒 Join to view"}
                  </span>
                </div>
              )}
            </button>
            <div className="p-4 rounded-xl bg-card border border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><Zap className="w-4 h-4 text-primary" /><span>Skill Level</span></div>
              <p className={`font-semibold capitalize ${getSkillClass(game.skill_level)}`}>{game.skill_level}</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2"><MapPin className="w-4 h-4 text-primary" /><span>Location</span></div>
            <p className="font-semibold">{game.location_name}</p>
            <p className="text-sm text-muted-foreground">{game.address}</p>
            <p className="text-sm mt-1">{getCityWithFlag(game.city)}</p>

            {/* Map Preview — coord branch, or address-search fallback for games
                created before geocoding was wired (lat/lng == 0). */}
            {resolvedCoords.hasCoords ? (
              <div className="mt-3 rounded-lg overflow-hidden border border-border">
                <iframe
                  title="Event Location"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${resolvedCoords.longitude - 0.005},${resolvedCoords.latitude - 0.003},${resolvedCoords.longitude + 0.005},${resolvedCoords.latitude + 0.003}&layer=mapnik&marker=${resolvedCoords.latitude},${resolvedCoords.longitude}`}
                  className="w-full h-32"
                  style={{ border: 0 }}
                />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${resolvedCoords.latitude},${resolvedCoords.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-muted/50 hover:bg-muted px-3 py-2 flex items-center justify-between text-sm transition-colors"
                >
                  <span className="text-muted-foreground">Open in Google Maps</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </a>
              </div>
            ) : resolvedCoords.loading ? (
              <div className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="game-map-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading map…
              </div>
            ) : (game.location_name || game.address) ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([game.location_name, game.address, game.city].filter(Boolean).join(", "))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 rounded-lg border border-border bg-muted/40 hover:bg-muted px-3 py-2 flex items-center justify-between text-sm transition-colors"
                data-testid="game-open-in-maps-fallback"
              >
                <span className="text-muted-foreground">Open in Google Maps</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </a>
            ) : null}
          </div>

          {/* Event Gallery */}
          <EventGallery 
            gameId={game.game_id}
            gameTitle={game.title}
            isOwner={isOwner} 
            isAttendee={hasBooked}
            isPastEvent={new Date(game.date) < new Date()}
          />

          <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border -mx-4 px-4 py-4 mt-8">
            {/* "Part of a Series" upsell — promotes the parent Series / Season Pass */}
            {seriesInfo && game.status === "active" && !hasBooked && (
              <button
                type="button"
                onClick={() => navigate(`/series/${seriesInfo.series_id}`)}
                className="w-full mb-3 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 hover:bg-primary/15 transition-colors px-3.5 py-2.5 text-left"
                data-testid="view-parent-series-btn"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-lg leading-none">🔁</span>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-primary-ink font-semibold">Part of a series</div>
                    <div className="text-sm font-medium text-foreground truncate">
                      {seriesInfo.season_pass_enabled && (seriesInfo.season_pass_current_price ?? seriesInfo.season_pass_price) > 0
                        ? `Save with the Season Pass · ${formatPrice(seriesInfo.season_pass_current_price || seriesInfo.season_pass_price, seriesInfo.currency || game.currency)}`
                        : `Explore all sessions · ${seriesInfo.title}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-semibold text-primary-ink shrink-0">
                  View series
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>
            )}

            <div className="flex items-center justify-between gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Price</span>
                <p className={`text-3xl font-bold ${game.price === 0 ? "text-primary" : "text-foreground"}`}>{formatPrice(game.price, game.currency)}</p>
                {waitlistCount > 0 && spotsLeft(game) === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{waitlistCount} on waitlist</p>
                )}
              </div>
              
              {/* Show different buttons based on booking/game status */}
              {hasBooked ? (
                // User already has a booking - show "You're Booked" 
                <div className="flex flex-col items-end gap-1">
                  <Button size="lg" disabled className="bg-foreground/10 text-foreground border border-border font-display text-lg px-8 h-14 cursor-default" data-testid="booked-btn">
                    <Check className="w-5 h-5 mr-2 text-primary" />
                    You're Booked
                  </Button>
                  <span className="text-xs text-muted-foreground">See you there!</span>
                </div>
              ) : spotsLeft(game) > 0 ? (
                // Game has spots - show Join Game button
                <Button size="lg" onClick={handleJoinGame} disabled={joining} className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 font-display text-lg px-8 h-14" data-testid="join-game-btn">
                  {joining ? (
                    <span className="flex items-center gap-2"><span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />Joining...</span>
                  ) : (<>Join Game<ChevronRight className="w-5 h-5 ml-1" /></>)}
                </Button>
              ) : waitlistStatus.on_waitlist ? (
                // User is on waitlist - show Leave Waitlist button
                <div className="flex flex-col items-end gap-1">
                  <Button size="lg" variant="outline" onClick={handleLeaveWaitlist} disabled={joiningWaitlist} className="font-display text-lg px-6 h-12 border-primary/50 text-primary hover:bg-primary/10" data-testid="leave-waitlist-btn">
                    {joiningWaitlist ? (
                      <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />...</span>
                    ) : "Leave Waitlist"}
                  </Button>
                  <span className="text-xs text-primary">Position #{waitlistStatus.position}</span>
                </div>
              ) : (
                // Game is full - show Join Waitlist button
                <Button size="lg" variant="secondary" onClick={handleJoinWaitlist} disabled={joiningWaitlist} className="font-display text-lg px-6 h-14 bg-muted hover:bg-muted/80" data-testid="join-waitlist-btn">
                  {joiningWaitlist ? (
                    <span className="flex items-center gap-2"><span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />...</span>
                  ) : (<>Join Waitlist<ChevronRight className="w-5 h-5 ml-1" /></>)}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Edit Game Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit Game</DialogTitle>
          </DialogHeader>
          {editData && (
            <div className="space-y-4 py-2">
              <div><Label>Title *</Label><Input value={editData.title} onChange={(e) => setEditData(p => ({ ...p, title: e.target.value }))} className="bg-muted/50" data-testid="edit-title" /></div>
              <div><Label>Description</Label><Textarea value={editData.description} onChange={(e) => setEditData(p => ({ ...p, description: e.target.value }))} className="bg-muted/50" rows={3} data-testid="edit-description" /></div>
              <div>
                <Label>Sport</Label>
                <Select value={editData.sport} onValueChange={(v) => setEditData(p => ({ ...p, sport: v }))}>
                  <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="football">Football</SelectItem>
                    <SelectItem value="padel">Padel</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Game Image</Label>
                <div className="mt-1 relative rounded-lg overflow-hidden border border-border bg-muted/30" style={{ aspectRatio: "16/9" }}>
                  <img src={imagePreview || editData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  {uploadingImage && (<div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>)}
                  <label htmlFor="edit-image-upload" className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs font-medium cursor-pointer transition-colors" data-testid="edit-upload-image-btn">
                    <Camera className="w-3.5 h-3.5" />{uploadingImage ? "Uploading..." : "Change Image"}
                  </label>
                  <input id="edit-image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date *</Label><Input type="date" value={editData.date} onChange={(e) => setEditData(p => ({ ...p, date: e.target.value }))} className="bg-muted/50" data-testid="edit-date" /></div>
                <div><Label>City *</Label><Input value={editData.city} onChange={(e) => setEditData(p => ({ ...p, city: e.target.value }))} className="bg-muted/50" data-testid="edit-city" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Time *</Label><Input type="time" value={editData.time_start} onChange={(e) => setEditData(p => ({ ...p, time_start: e.target.value }))} className="bg-muted/50" /></div>
                <div><Label>End Time *</Label><Input type="time" value={editData.time_end} onChange={(e) => setEditData(p => ({ ...p, time_end: e.target.value }))} className="bg-muted/50" /></div>
              </div>
              <div><Label>Venue Name *</Label><Input value={editData.location_name} onChange={(e) => setEditData(p => ({ ...p, location_name: e.target.value }))} className="bg-muted/50" /></div>
              <div><Label>Address</Label><Input value={editData.address} onChange={(e) => setEditData(p => ({ ...p, address: e.target.value }))} className="bg-muted/50" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Max Players</Label><Input type="number" min={2} value={editData.max_players} onChange={(e) => setEditData(p => ({ ...p, max_players: parseInt(e.target.value) || 2 }))} className="bg-muted/50" /></div>
                <div><Label>Price</Label><Input type="number" min={0} step={0.5} value={editData.price} onChange={(e) => setEditData(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} className="bg-muted/50" /></div>
                <div>
                  <Label>Skill</Label>
                  <Select value={editData.skill_level} onValueChange={(v) => setEditData(p => ({ ...p, skill_level: v }))}>
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Your Contact (optional)</Label>
                <Input
                  type="text"
                  placeholder="Phone, WhatsApp, Instagram, etc."
                  value={editData.host_contact || ""}
                  onChange={(e) => setEditData(p => ({ ...p, host_contact: e.target.value }))}
                  className="bg-muted/50"
                  data-testid="edit-host-contact"
                />
                <p className="text-xs text-muted-foreground mt-1">Visible to players who joined the game.</p>
              </div>

              <div>
                <Label>Meeting Info (optional)</Label>
                <Textarea
                  placeholder="e.g. We meet at the entrance gate 10 min early, bring a light + dark shirt, parking on Maple St."
                  value={editData.meeting_info || ""}
                  onChange={(e) => setEditData(p => ({ ...p, meeting_info: e.target.value }))}
                  className="bg-muted/50 min-h-[80px]"
                  rows={3}
                  data-testid="edit-meeting-info"
                />
                <p className="text-xs text-muted-foreground mt-1">Where to meet, what to bring, how to find you. Visible to players who joined the game.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-edit-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Players List Modal */}
      <Dialog open={showPlayersModal} onOpenChange={setShowPlayersModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Players ({allAttendees.length})</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {allAttendees.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No players yet</p>
            ) : (
              <div className="divide-y divide-border">
                {allAttendees.map((player) => (
                  <button
                    key={player.user_id}
                    onClick={() => {
                      if (player.user_id && !player.user_id.startsWith('fake_')) {
                        setShowPlayersModal(false);
                        setProfileUserId(player.user_id);
                      }
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left"
                    disabled={player.user_id?.startsWith('fake_')}
                  >
                    <Avatar className="h-10 w-10">
                      {player.picture && <AvatarImage src={player.picture} />}
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {player.name ? player.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium flex-1">{player.name}</span>
                    {!player.user_id?.startsWith('fake_') && (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <UserProfileModal 
        userId={profileUserId}
        isOpen={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </div>
  );
};

export default GamePage;
